/* Putting a contract into the leveraged book.
 *
 * There was no way to add one at all — the book carried whatever the seed gave
 * it and nothing else. Two things this asks for and one it refuses to:
 *
 * It searches the exchange's board by name, because gold is XAU_USDT and oil is
 * USOIL_USDT and nobody knows that. And it asks for margin and leverage rather
 * than for size, deriving the notional itself — the "Size (USDT)" figure on a
 * position card is contracts times the current mark and drifts every tick, and
 * feeding that to the §5.1 formula is what once overstated a position by 15%. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { searchContracts, type ContractHit } from '../api/client';
import { liquidationFor } from '../state/liquidation';
import { price as fmtLiq } from '../lib/format';

/* A contract typed in rather than found.
 *
 * Adding one used to require a search result, so the day the exchange stopped
 * answering the search nothing could be added at all — not even a position whose
 * every figure was on the screen in front of you. The symbol is what the marks
 * are fetched by; a wrong one shows as "no mark" and is changed under Correct. */
function typedContract(typed: string): ContractHit {
  const raw = typed.trim().toUpperCase().replace(/\s+/g, '');
  const symbol = raw.includes('_') ? raw : `${raw}_USDT`;
  const first = symbol.split('_')[0] ?? symbol;
  const base = first.endsWith('STOCK') && first.length > 5 ? first.slice(0, -5) : first;
  return { symbol, name: symbol, base, maxLeverage: null };
}
import { newId } from '../lib/id';
import { decimal } from '../lib/number';
import { useStore } from '../state/store';
import type { Perp } from '../types';

const SETTLE_MS = 220;

export function AddContract({ say }: { say: (m: string) => void }) {
  const { openPerp } = useStore();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<ContractHit[]>([]);
  const [picked, setPicked] = useState<ContractHit | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const inFlight = useRef<AbortController | null>(null);

  const [side, setSide] = useState<'long' | 'short'>('long');
  const [leverage, setLeverage] = useState('5');
  const [margin, setMargin] = useState('');
  /* Either figure will do: the margin posted, or the position's size in USDT.
     The other follows from the leverage, so asking for both was asking for a
     number the form could work out — and a chance for the two to disagree. */
  const [sizing, setSizing] = useState<'margin' | 'size'>('margin');
  const [size, setSize] = useState('');
  const [entry, setEntry] = useState('');
  const [liq, setLiq] = useState('');
  const [takeProfit, setTakeProfit] = useState('');

  useEffect(() => {
    const q = query.trim();
    if (!open || q.length < 1) { setHits([]); return; }
    const timer = window.setTimeout(() => {
      inFlight.current?.abort();
      const ctrl = new AbortController();
      inFlight.current = ctrl;
      void searchContracts(q, ctrl.signal)
        .then((rows) => { if (!ctrl.signal.aborted) setHits(rows); })
        .catch(() => { if (!ctrl.signal.aborted) setNote('The exchange search did not answer. The symbol can still be typed and added by hand.'); });
    }, SETTLE_MS);
    return () => window.clearTimeout(timer);
  }, [query, open]);

  const reset = useCallback(() => {
    setOpen(false); setQuery(''); setHits([]); setPicked(null); setNote(null);
    setSide('long'); setLeverage('5'); setMargin(''); setSize(''); setSizing('margin'); setEntry(''); setLiq(''); setTakeProfit('');
  }, []);

  /* Read with `decimal`, not `Number`: the figures here get copied off a MEXC
     card, and a Polish keyboard types the separator as a comma. */
  const numbers = {
    leverage: decimal(leverage),
    // Given as size, the margin is that size at this leverage.
    margin: sizing === 'size' ? decimal(size) / decimal(leverage) : decimal(margin),
    entry: decimal(entry),
  };
  /* The liquidation price as typed, or estimated from entry and leverage when
     the field is left empty. See `state/liquidation.ts`. */
  const liquidation = liquidationFor(decimal(liq), side, numbers.entry, numbers.leverage);
  const ready = picked && liquidation !== null
    && Object.values(numbers).every((n) => Number.isFinite(n) && n > 0);
  const notional = ready ? numbers.margin * numbers.leverage : 0;

  const save = async () => {
    if (!picked || !ready || !liquidation) {
      return setNote('Margin, leverage and entry are needed. The liquidation price is estimated if left empty, and the target is optional.');
    }

    const perp: Perp = {
      id: newId('perp'),
      symbol: picked.symbol,
      display: picked.base || picked.symbol,
      side,
      leverage: numbers.leverage,
      // Notional at entry: margin times leverage, never the card's "Size (USDT)".
      sizeUsdt: notional,
      marginUsdt: numbers.margin,
      entryPrice: numbers.entry,
      liqPrice: liquidation.price,
      ...(liquidation.estimated ? { liqEstimated: true } : {}),
      // Recorded from now on, so a closed contract can say how long it was held.
      openedAt: new Date().toISOString(),
    };
    const tp = decimal(takeProfit);
    if (Number.isFinite(tp) && tp > 0) perp.takeProfit = tp;

    await openPerp(perp);
    say(`${perp.display} added. ${numbers.margin} margin at ${numbers.leverage}x, `
      + 'taken out of the free balance on the exchange.');
    reset();
  };

  if (!open) {
    return (
      <div className="fab-row">
        <button className="btn btn-block" onClick={() => setOpen(true)}>Add a contract</button>
      </div>
    );
  }

  return (
    <div className="card card-pad" style={{ marginTop: 10 }}>
      <div className="field">
        <label htmlFor="contract-q">Contract</label>
        <input
          id="contract-q" className="input" autoComplete="off"
          placeholder="gold, silver, oil, MU…"
          value={picked ? `${picked.name}` : query}
          onChange={(e) => { setPicked(null); setQuery(e.target.value); }}
        />
        {!picked && hits.length > 0 ? (
          <div className="suggestions">
            {hits.map((h) => (
              <button key={h.symbol} className="suggestion" onClick={() => { setPicked(h); setHits([]); }}>
                <span className="suggestion-symbol num">{h.symbol}</span>
                <span className="suggestion-name">{h.name}</span>
                {h.maxLeverage ? <span className="suggestion-where">to {h.maxLeverage}x</span> : null}
              </button>
            ))}
          </div>
        ) : null}
        {!picked && query.trim().length >= 2 ? (
          <>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 8 }}
                    onClick={() => { setPicked(typedContract(query)); setHits([]); setNote(null); }}>
              Use {typedContract(query).symbol} as typed
            </button>
            <div className="hint">
              When the search finds nothing or does not answer. A share contract is its name with
              STOCK added, so Micron is <span className="num">MUSTOCK_USDT</span>. A wrong symbol
              shows as &ldquo;no mark&rdquo; and can be changed under Correct.
            </div>
          </>
        ) : null}
      </div>

      {picked ? (
        <>
          <div className="seg" style={{ marginBottom: 12 }}>
            {(['long', 'short'] as const).map((s) => (
              <button key={s} aria-pressed={side === s} onClick={() => setSide(s)}>
                {s === 'long' ? 'Long' : 'Short'}
              </button>
            ))}
          </div>

          <div className="seg" style={{ marginBottom: 10 }}>
            <button aria-pressed={sizing === 'margin'} onClick={() => setSizing('margin')}>Margin</button>
            <button aria-pressed={sizing === 'size'} onClick={() => setSizing('size')}>Size</button>
          </div>

          <div className="control-row">
            <div className="field" style={{ flex: 1 }}>
              {sizing === 'margin' ? (
                <>
                  <label htmlFor="c-margin">Margin posted (USDT)</label>
                  <input id="c-margin" className="input" data-mono="true" inputMode="decimal"
                         value={margin} onChange={(e) => setMargin(e.target.value)} />
                </>
              ) : (
                <>
                  <label htmlFor="c-size">Position size (USDT)</label>
                  <input id="c-size" className="input" data-mono="true" inputMode="decimal"
                         value={size} onChange={(e) => setSize(e.target.value)} />
                </>
              )}
            </div>
            <div className="field" style={{ flex: '0 0 96px' }}>
              <label htmlFor="c-lev">Leverage</label>
              <input id="c-lev" className="input" data-mono="true" inputMode="decimal"
                     value={leverage} onChange={(e) => setLeverage(e.target.value)} />
            </div>
          </div>

          {sizing === 'size' ? (
            <div className="hint" style={{ marginTop: -6, marginBottom: 12 }}>
              {Number.isFinite(numbers.margin) && numbers.margin > 0
                ? <>So <span className="num">{numbers.margin.toFixed(2)} USDT</span> of margin at {numbers.leverage}x. </>
                : null}
              The size at the moment of entry. MEXC&rsquo;s &ldquo;Size (USDT)&rdquo; on an open
              position follows the current price, so copied later it is a little off — the margin is
              then the exact figure to give.
            </div>
          ) : null}

          <div className="control-row">
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="c-entry">Entry price</label>
              <input id="c-entry" className="input" data-mono="true" inputMode="decimal"
                     value={entry} onChange={(e) => setEntry(e.target.value)} />
            </div>
            <div className="field" style={{ flex: 1 }}>
              <label htmlFor="c-liq">Liquidation, if known</label>
              <input id="c-liq" className="input" data-mono="true" inputMode="decimal"
                     placeholder="estimate it"
                     value={liq} onChange={(e) => setLiq(e.target.value)} />
            </div>
          </div>

          {liquidation?.estimated ? (
            <div className="hint" style={{ marginTop: -6, marginBottom: 12 }}>
              Left empty, so estimated at <span className="num">≈ {fmtLiq(liquidation.price)}</span> from
              the entry and the leverage. It is shown as an estimate until the exchange&rsquo;s
              figure replaces it: typed in under Correct, or read with the MEXC key. The real one
              is usually a little further away, so this errs on the careful side.
            </div>
          ) : null}

          <div className="field">
            <label htmlFor="c-tp">Target, if you have one</label>
            <input id="c-tp" className="input" data-mono="true" inputMode="decimal"
                   value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} />
          </div>

          {ready ? (
            <div className="kv" style={{ marginBottom: 10 }}>
              <span className="k">Notional at entry</span>
              <span className="v num">{notional.toLocaleString('en-US', { maximumFractionDigits: 4 })} USDT</span>
            </div>
          ) : null}

          <div className="dim" style={{ fontSize: 11, marginBottom: 10, lineHeight: 1.45 }}>
            Margin times leverage, worked out here rather than copied. The
            &ldquo;Size (USDT)&rdquo; on the exchange&rsquo;s card is contracts times the current
            mark and moves every tick; using it overstates the result by however far the position
            has already travelled.
          </div>
        </>
      ) : null}

      {note ? <div className="warn" style={{ fontSize: 11.5, marginBottom: 10 }}>{note}</div> : null}

      <div className="fab-row" style={{ marginTop: 0 }}>
        <button className="btn btn-block" onClick={reset}>Cancel</button>
        <button className="btn btn-primary btn-block" disabled={!ready} onClick={() => void save()}>
          Add it
        </button>
      </div>
    </div>
  );
}
